import { useEffect, useRef, useState } from "react";

type Props = {
  src: string;
  title: string;
  height?: number;
};

/**
 * Renders the Google Maps embed only once it is close to the viewport, so the
 * map's script/network work never competes with the initial page render.
 */
export function LazyMapFrame({ src, title, height = 360 }: Props) {
  const holderRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = holderRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <div ref={holderRef} style={{ minHeight: height }}>
      {visible ? (
        <iframe
          title={title}
          src={src}
          width="100%"
          height={height}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          style={{ border: 0, display: "block" }}
        />
      ) : null}
    </div>
  );
}
